// @ts-strict-ignore
import React, { useContext, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  ButtonSize,
  ButtonVariant,
  IconName,
  Text,
} from '@usealloy/component-library';

import {
  StyledCategoryContainer,
  StyledDeactivatedCategoryContainer,
  CategoryInfo,
  StyledPre,
  RouteActions,
} from './styles';
import RoutingServiceDropdown from './RoutingServiceDropdown';
import type { DataRoutingCategories } from '~/dashboard/views/workflows/service/dataRoutes/types';
import type { DataRoute } from '~/dashboard/views/workflows/store/engine/types';
import {
  addDeactivatedLegacyDataRoute,
  createDataRoute,
  removeDataRoute,
  updateDataRouteValue,
  setServiceRoutingCategoryNameInEditing,
} from '~/dashboard/views/workflows/store/redux/slice';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { selectDiffMode, selectFocusedAction } from 'flow/store/redux/selectors';
import { PLACEHOLDER_DIFF_STATUS } from '../../diffStyles';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';
import { createWorkflowHistoryThunk } from 'flow/store/redux/thunks';
import { Operation } from 'flow/service/changelog/types';
import { UserContext } from '~/dashboard/context';
import { getDatarouteHtmlElementId } from 'flow/editor/hooks/useWorkflowHistory/helpers';


interface RoutingCategoryProps {
  mode: 'incoming' | 'outgoing';
  dataRoutingCategory: DataRoutingCategories;
  isEditing: boolean;
  isDynamicDataRoutingEnabled: boolean;
  isDeactivated?: boolean;
  isDisabled?: boolean;
}

export default function RoutingCategory({
  mode,
  dataRoutingCategory,
  isEditing,
  isDynamicDataRoutingEnabled,
  isDisabled = false,
  isDeactivated = false,
}: RoutingCategoryProps) {
  const dispatch = useAppDispatch();
  const isInDiffMode = useAppSelector(selectDiffMode);
  const [dataRouteForCategory, setDataRouteForCategory] = useState<DataRoute>(
    dataRoutingCategory.dataRoute,
  );
  const focusedAction = useAppSelector(selectFocusedAction);
  const { featureFlags } = useContext(UserContext);

  // Re-hydrate local state when the persisted route changes from outside this row
  // (e.g. undo/redo), so the dropdown reflects the restored selection. This row is keyed
  // by category id, so it isn't remounted and the useState initializer won't re-run.
  useEffect(() => {
    setDataRouteForCategory(dataRoutingCategory.dataRoute);
  }, [dataRoutingCategory.dataRoute]);

  const uiEffectHtmlElementId = getDatarouteHtmlElementId(dataRoutingCategory.category.name);

  const onClickEdit = () => {
    if (!isEditing) {
      dispatch(setServiceRoutingCategoryNameInEditing(dataRoutingCategory.category.name));
      return;
    }

    const stateChange = () => {
      if (dataRoutingCategory.dataRoute) {
        // means this is editing existing data routing configuration
        dispatch(
          updateDataRouteValue({
            path: dataRouteForCategory.publicId,
            value: dataRouteForCategory,
          }),
        );
      } else if (dataRouteForCategory) {
        dispatch(
          createDataRoute({
            path: dataRouteForCategory.publicId,
            value: dataRouteForCategory,
            expressionPath: '',
          }),
        );
      }
      dispatch(setServiceRoutingCategoryNameInEditing(null));
    };

    dispatch(
      createWorkflowHistoryThunk(
        stateChange,
        Boolean(featureFlags?.workflowEditorUndoRedo),
        {
          operation: Operation.Modify,
          uiEffectContext: createUiEffectContext(changeDescription.dataroute,
            {
              htmlElementId: uiEffectHtmlElementId,
              actionId: focusedAction.publicId,
            },
          ),
        },
      ),
    );
  };

  const onClickRemove = () => {
    const stateChange = () => {
      dispatch(
        removeDataRoute({
          id: dataRouteForCategory.publicId,
        }),
      );
    };

    dispatch(
      createWorkflowHistoryThunk(
        stateChange,
        Boolean(featureFlags?.workflowEditorUndoRedo),
        {
          operation: Operation.Remove,
          uiEffectContext: createUiEffectContext(changeDescription.dataroute,
            {
              htmlElementId: uiEffectHtmlElementId,
              actionId: focusedAction.publicId,
            },
          ),
        },
      ),
    );

    setDataRouteForCategory(null);
  };

  const onClickDeactivate = () => {
    const stateChange = () => {
      dispatch(addDeactivatedLegacyDataRoute({
        mode,
        category: dataRoutingCategory.category,
        dataRoute: dataRouteForCategory,
      }));
      dispatch(removeDataRoute({
        id: dataRouteForCategory.publicId,
      }));
    };

    dispatch(
      createWorkflowHistoryThunk(
        stateChange,
        Boolean(featureFlags?.workflowEditorUndoRedo),
        {
          operation: Operation.Modify,
          uiEffectContext: createUiEffectContext(changeDescription.dataroute,
            {
              htmlElementId: uiEffectHtmlElementId,
              actionId: focusedAction.publicId,
            },
          ),
        },
      ),
    );

    setDataRouteForCategory(null);
  };

  const Container = isDeactivated ? StyledDeactivatedCategoryContainer : StyledCategoryContainer;

  // TODO: hardcoded until the per-route diff status is wired in. The diff-mode guard is
  // redundant once that lookup returns null outside diff mode, and can be dropped then.
  const diffStatus = isInDiffMode ? PLACEHOLDER_DIFF_STATUS : null;

  return (
    <Container
      diffStatus={diffStatus}
      id={uiEffectHtmlElementId}
    >
      <CategoryInfo>
        <Text as="span" size={2} weight={400} style={{ color: 'var(--cl-gray-700)' }}>
          Data
        </Text>
        <StyledPre>{dataRoutingCategory.category.name}</StyledPre>
      </CategoryInfo>

      <CategoryInfo>
        <Text as="span" size={2} weight={400} style={{ color: 'var(--cl-gray-700)' }}>
          Routed {mode === 'incoming' ? 'From' : 'To'}
        </Text>
        <RoutingServiceDropdown
          mode={mode}
          category={dataRoutingCategory.category}
          isEditing={!isDeactivated && isEditing}
          dataRouteForCategory={dataRouteForCategory}
          setDataRouteForCategory={setDataRouteForCategory}
        />
      </CategoryInfo>

      {isDeactivated ? (
        <Badge status="disabled">Deactivated</Badge>
      ) : (
        <RouteActions>
          {isDynamicDataRoutingEnabled ? (
            <Button
              variant={ButtonVariant.primary}
              size={ButtonSize.small}
              disabled={!dataRouteForCategory || isDisabled}
              onClick={onClickDeactivate}
            >
              Deactivate
            </Button>
          ) : (
            <>
              <Button
                icon={isEditing ? IconName.Approved : IconName.Edit}
                variant={ButtonVariant.tertiary}
                onClick={onClickEdit}
                disabled={isDisabled}
              />
              <Button
                icon={IconName.Trash}
                variant={ButtonVariant.tertiary}
                disabled={!dataRouteForCategory || isDisabled}
                onClick={onClickRemove}
              />
            </>
          )}
        </RouteActions>
      )}
    </Container>
  );
}
